import { action, computed, extendObservable, observable, reaction } from "mobx";
import { IListProvider } from "shared/_common/list/IListProvider";
import { LoadingStateMdl } from "shared/_common/loaders/_models/LoadingStateMdl";
import { createLoadingStateFromPromise } from "shared/_common/loaders/loadingStateUtils";
import { ObservableArray } from "mobx/lib/types/observablearray";
import { TFilter } from "admin/_common/filters/TFilter";
import _ from "lodash";
import { TObjWithId } from "_common/types/GenericTypes";
import { DEFAULT_PAGE_SIZE } from "_common/resources/BaseResourceStore";

export class ListStore<T extends TObjWithId> {
    @observable items: (T | undefined)[] = [];
    @observable count: number | undefined;
    @observable selectedPage: number;
    @observable pageSize: number;
    @observable pageSizeList: number[] = [2, 5, 10, 15, 20, 50];
    @observable sort: { [key: string]: number } | undefined;
    @observable filters: TFilter[] = [];

    readonly listId: string;
    @observable private loadingStates: {
        [page: number]: LoadingStateMdl<{ count: number; items: T[] }>;
    } = {};
    private readonly listProvider: IListProvider<T>;
    private resetAndReload = _.debounce(
        action(() => {
            this.reset();
            this.setSelectedPage(1);
        }),
        1000,
    );

    constructor(
        listId: string,
        listProvider: IListProvider<T>,
        initialState?: { count: number; pages: { [offset: number]: T[] }; page?: number },
        pageSize?: number,
        noInitialLoading?: boolean,
        initialFilters?: TFilter[],
        initialSort?: { [key: string]: number },
        initialPage?: number,
    ) {
        this.listId = listId;
        this.listProvider = listProvider;
        this.selectedPage = initialState?.page ?? initialPage ?? 1;
        this.pageSize = pageSize ?? DEFAULT_PAGE_SIZE;
        if (initialState) this.init(initialState);
        if (initialFilters && initialFilters.length > 0) this.filters = initialFilters;
        if (initialSort) this.sort = initialSort;
        if (!noInitialLoading) this.load();

        reaction(
            () => this.totalPages,
            () => {
                this.setSelectedPage(1);
            },
        );
    }

    @computed get paginatedItems() {
        const offset = (this.selectedPage - 1) * this.pageSize;
        return this.items.slice(offset, offset + this.pageSize);
    }

    @computed get currentLoadingState() {
        return this.loadingStates[this.selectedPage];
    }

    @computed get hasMorePage() {
        return this.count === undefined || this.items.length < this.count;
    }

    @action load(page: number = this.selectedPage, isShuffle = false) {
        if (!this.loadingStates[page] || this.loadingStates[page].status === "IDLE") {
            const offset = (page - 1) * this.pageSize;
            if (this.items[offset]) {
                extendObservable(this.loadingStates, {
                    [page]: new LoadingStateMdl("SUCCEEDED"),
                });
            } else {
                const loadPromise = this.listProvider
                    .list(offset, this.pageSize, this.listId, this.sort, this.filters, isShuffle)
                    .then(
                        action((result) => {
                            const { count, items } = result;
                            this.count = count;

                            if (this.selectedPage > this.totalPages) {
                                this.selectedPage = 1;
                            }

                            if (offset > this.items.length) {
                                this.items.push(...new Array(offset - this.items.length).fill(undefined));
                            }
                            for (let i = 0; i < items.length; i++) {
                                this.items[offset + i] = items[i];
                            }
                            this.loadingStates[page].setSuccess(result);
                            return result;
                        }),
                        (err) => this.loadingStates[page].setError(err),
                    );
                if (!this.loadingStates[page]) {
                    extendObservable(this.loadingStates, {
                        [page]: createLoadingStateFromPromise(loadPromise),
                    });
                } else {
                    this.loadingStates[page].startLoading(loadPromise as any);
                }
            }
        }
        return this.loadingStates[page];
    }

    @computed get totalPages() {
        return this.count ? Math.ceil(this.count / this.pageSize) : 1;
    }

    @action setPageSize(pageSize: number) {
        this.pageSize = pageSize;
        this.resetAndReload();
    }

    @action setSelectedPage(page: number, isShuffle = false) {
        if (page > this.totalPages) {
            page = 1;
        }

        this.selectedPage = page;
        return this.load(page, isShuffle);
    }

    @action setSort(sort: { [key: string]: number }) {
        this.sort = sort;
        this.resetAndReload();
    }

    @action updateSort(sort: { [key: string]: number }) {
        this.sort = sort;
    }

    getFilterByFilterId(filterId: string) {
        const filterIndex = this.getFilterIndex(filterId);
        if (filterIndex > -1) return this.filters[filterIndex];
        else return undefined;
    }

    getFilterIndex(filterId: string, value?: any) {
        return this.filters.findIndex(
            (filter) => filter.id === filterId && (value === undefined || filter.value === value),
        );
    }

    @action updateFilter(filter: TFilter, index?: number) {
        if (index === undefined) this.filters.push(filter);
        else this.filters.splice(index, 1, filter);
    }

    updateFilters(filters: TFilter[]) {
        this.filters = [];
        filters.forEach(
            action((filter) => {
                this.filters.push(filter);
            }),
        );
    }

    @action updateFilterAndReload(filter: TFilter, index?: number) {
        this.updateFilter(filter, index);
        this.reset();
        this.selectedPage = 1;
        this.load();
    }
    @action updateFiltersAndReload(filter: TFilter[]) {
        this.updateFilters(filter);
        this.resetAndReload();
    }

    @action removeFilters() {
        this.filters.splice(0, this.filters.length);
    }

    @action removeFilter(index: number) {
        this.filters.splice(index, 1);
    }

    @action removeFilterByFilterId(filterId: string, value?: any) {
        while (this.getFilterIndex(filterId, value) > -1) {
            this.removeFilter(this.getFilterIndex(filterId));
        }
    }

    @action removeFiltersExceptFollowingIds(ids: string[]) {
        this.filters = this.filters.filter((f) => ids.includes(f.id));
        this.reload();
    }

    @action removeFilterAndReload(index: number) {
        this.removeFilter(index);
        this.resetAndReload();
    }

    @action reset() {
        this.loadingStates = {};
        ((this.items as unknown) as ObservableArray<void>).clear();
    }

    reload() {
        this.reset();
        this.load();
    }

    @action onCreate(item: T) {
        if (this.items.find((listItem) => listItem?._id === item._id)) {
            this.onUpdate(item);
            return;
        }
        this.items.unshift(item);
    }

    @action onUpdate(updatedItem: T) {
        this.items.splice(
            this.items.findIndex((item) => item?._id === updatedItem._id),
            1,
            updatedItem,
        );
    }

    @action onDelete(itemId: string) {
        this.items.splice(
            this.items.findIndex((item) => item?._id === itemId),
            1,
        );
    }

    private init(initialState: { count: number; pages: { [offset: number]: T[] }; page?: number }) {
        this.count = initialState.count;
        this.selectedPage = initialState.page && initialState.page > 0 ? initialState.page : 1;
        Object.keys(initialState.pages)
            .map(Number)
            .forEach((offset) => {
                const page = offset + 1;
                this.items = [...this.items, ...initialState.pages[offset]];
                this.loadingStates[page] = new LoadingStateMdl("SUCCEEDED");
                if (this.selectedPage !== page) {
                    this.loadingStates[page] = new LoadingStateMdl("IDLE");
                }
            });
    }
}
